import React, { useState } from 'react';
import styled from 'styled-components';

import { tableHelper } from '@builttocreate/engine-utils';
import InputSettingsField from './InputSettingsField';
import OptionSettings from './OptionSettings';
import MetadataSettings from './MetadataSettings';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import Button from '../../misc/Button';
import AccordionSettings from './AccordionSettings';
import { StyledFieldWrapper, StyledButtonsWrapper, DividerLine } from './SettingsCommonElements';

import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import Popconfirm from '../../misc/Popconfirm';
import ThemeIcons from '../../../theme/Icons';

const { getColumnOrder } = tableHelper;

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const FieldSettingsTableColumn = ({

  fieldId,
  fieldPositionId,
  fieldIdentifier,
  columnId,

  activeFieldPosition,

  identifier,

  title,
  type,
  displayType,
  columnType,
  columnOperand,
  value,
  options,
  width,
  maxImageHeight,
  maxImageWidth,
  metadata,
  config,

  //Action handlers
  onChange,
  onOptionCreate,
  onOptionChange,
  onOptionDelete,

  /**
   * onFieldColumnDelete is used for
   * 1.Removing the active column settings selection and hiding the column settings panel.
   * 2.Deleting the column
   */
  onFieldColumnDelete,

  tableColumns,
  tableColumnOrder,

}) => {

  const [displayConfirm, setDisplayConfirm] = useState(false);

  const handleColumnUpdate = (columnId, tableColumns, tableColumnOrder, data) => {

    const nextColumns = [...tableColumns];

    const index = nextColumns.findIndex((col) => col._id === columnId);

    const payload = {};

    if (data && data.deleted) {

      nextColumns.splice(index, 1);

      const nextColumnOrder = getColumnOrder(null, nextColumns);

      payload.tableColumns = nextColumns;

      payload.tableColumnOrder = nextColumnOrder;

    }
    else {

      nextColumns[index] = { ...nextColumns[index], ...data };

      payload.tableColumns = nextColumns;

      if (data && data._id && tableColumnOrder) {

        const nextColumnOrder = [...tableColumnOrder];

        const columnIndex = nextColumnOrder.findIndex((col) => col === columnId);

        nextColumnOrder.splice(columnIndex, 1, data._id.replace(/\s/g, ''));

        payload.tableColumnOrder = nextColumnOrder;

      }
    }

    onChange(fieldId, fieldIdentifier, fieldPositionId, payload);

  };

  const validOptions = [];
  if (options && options.length > 0) {
    options.forEach((option) => {
      if (!option.deleted) validOptions.push(option);
    });
  }

  let defaultValueNode = null;

  if (columnType === FieldTableColumnTypes.text) {
    defaultValueNode = (
      <StyledFieldWrapper>
        <InputSettingsField
          fieldId={fieldId}
          label="Default Value"
          description="Default new row cell value"
          value={value}
          type="text"
          onChange={(i, value) => handleColumnUpdate(columnId, tableColumns, tableColumnOrder, { value })}
        />
      </StyledFieldWrapper>
    );
  } else if (columnType === FieldTableColumnTypes.dropdown) {
    defaultValueNode = (
      <StyledFieldWrapper>
        <InputSettingsField
          type="dropdown"
          fieldId={fieldId}
          label="Default Value"
          description="Default new row cell value"
          value={value}
          options={validOptions}
          onChange={(i, value) => handleColumnUpdate(columnId, tableColumns, tableColumnOrder, { value })}
        />
      </StyledFieldWrapper>
    );
  }

  let imageSettingsNode = null;

  if (columnType === FieldTableColumnTypes.image) {
    imageSettingsNode = (
      <>
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId={fieldId}
            id={fieldId}
            label="Image Width"
            value={maxImageWidth}
            type="number"
            onChange={(i, value) => handleColumnUpdate(columnId, tableColumns, tableColumnOrder, { maxImageWidth: value || 0 })}
          />
        </StyledFieldWrapper>
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId={fieldId}
            id={fieldId}
            label="Image Height"
            value={maxImageHeight}
            type="number"
            onChange={(i, value) => handleColumnUpdate(columnId, tableColumns, tableColumnOrder, { maxImageHeight: value || 0 })}
          />
        </StyledFieldWrapper>
        <DividerLine />
      </>
    );
  }

  return (
    <StyledWrapper key={fieldId} data-testid={`${fieldId}-field-settings-table-column`}>
      <FieldSettingsHeaderTitle
        title={`${columnOperand}: Column Settings`}
        icon={ThemeIcons.fields.tableColumn}
      />
      <StyledFieldWrapper>
        <InputSettingsField
          fieldId={fieldId}
          value={title}
          label="Title"
          type="text"
          onChange={(i, value) => handleColumnUpdate(columnId, tableColumns, tableColumnOrder, { title: value })}
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <InputSettingsField
          fieldId={fieldId}
          label="Width"
          value={width}
          type="number"
          onChange={(i, value) => handleColumnUpdate(columnId, tableColumns, tableColumnOrder, { width: parseFloat(value, 10) })}
          onClear={() => handleColumnUpdate(columnId, tableColumns, tableColumnOrder, { width: 0 })}
        />
      </StyledFieldWrapper>
      {defaultValueNode}
      <DividerLine />
      {imageSettingsNode}
      {columnType !== FieldTableColumnTypes.dropdown ? null : (
        <>
          <StyledFieldWrapper>
            <OptionSettings
              id="options"
              config={config?.options}
              activeFieldPosition={activeFieldPosition}
              type={type}
              displayType={displayType}
              options={options}
              onChange={(optionId, data) => onOptionChange(fieldId, fieldIdentifier, fieldPositionId, optionId, data)}
              onDelete={(optionId) => onOptionDelete(fieldId, fieldIdentifier, fieldPositionId, optionId)}
              onCreate={(data) => onOptionCreate(fieldId, fieldIdentifier, fieldPositionId, data)}
            />
          </StyledFieldWrapper>
          <DividerLine />
        </>
      )}
      {!config?.metadata ? null : (
        <>
          <MetadataSettings
            metadata={metadata}
            metadataOptions={config?.metadata}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              handleColumnUpdate(columnId, tableColumns, tableColumnOrder, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      <DividerLine style={{ marginBottom: 0 }} />
      <AccordionSettings
        title={config?.identifier?.label || 'Identifier'}
        height={60}
      >
        <IdentifierSelectorSettings
          fieldId={fieldId}
          fieldType={type}
          type="text"
          customValue={config?.identifier?.options}
          enableCustomOption={config?.identifier?.custom}
          value={identifier}
          onChange={(i, value) => handleColumnUpdate(columnId, tableColumns, tableColumnOrder, { identifier: value })}
        />
      </AccordionSettings>
      <DividerLine />
      <StyledButtonsWrapper>
        <Button
          danger
          icon={ThemeIcons.settings.delete}
          text="Delete"
          onClick={() => {
            setDisplayConfirm(true);
          }}
        />
        <Popconfirm
          title="Confirm Column Delete"
          description="Deletion will remove the table column and associated individual table cell fields"
          isOpen={displayConfirm}
          positions={['left']} // preferred positions by priority
          padding={18}
          cancelText="Cancel"
          onCancel={() => setDisplayConfirm(false)}
          confirmText="Confirm"
          onConfirm={() => {

            setDisplayConfirm(false);

            if (onFieldColumnDelete) onFieldColumnDelete(fieldId, fieldIdentifier, fieldPositionId, columnId);

          }}
        >
          <div />
        </Popconfirm>
      </StyledButtonsWrapper>
    </StyledWrapper>
  );

};

export default FieldSettingsTableColumn;
